import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { api, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { toast } from "@/lib/toast";
import { formatMoney } from "@/lib/currency";
import { Plus, Target, Trash2, Loader2, TrendingUp, History, DollarSign, Calendar, PieChart, Pencil } from "lucide-react";

const GOAL_COLORS = ["#0e7490", "#f97316", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899"];

const SavingsPage = () => {
  const { user } = useAuth();
  const { isExpired } = useOutletContext() || {};
  const userCurrency = user?.currency || "COP";
  
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositNote, setDepositNote] = useState("");
  const [allocations, setAllocations] = useState({});
  
  const [formData, setFormData] = useState({
    name: "",
    target_amount: "",
    deadline: "",
    currency: userCurrency
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const res = await api.get("/savings-goals");
      setGoals(res.data);
      // Initialize allocations
      const allocs = {};
      res.data.forEach(g => {
        allocs[g.id] = g.allocation_percentage || 0;
      });
      setAllocations(allocs);
    } catch (error) {
      toast.error("Error al cargar metas");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.target_amount) {
      toast.error("Completa los campos requeridos");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post("/savings-goals", {
        name: formData.name,
        target_amount: parseFloat(formData.target_amount),
        deadline: formData.deadline || null,
        currency: userCurrency
      });
      toast.success("Meta creada exitosamente");
      setDialogOpen(false);
      setFormData({ name: "", target_amount: "", deadline: "", currency: userCurrency });
      fetchGoals();
    } catch (error) {
      toast.error("Error al crear meta");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/savings-goals/${id}`);
      toast.success("Meta eliminada");
      fetchGoals();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleAddDeposit = async () => {
    if (!selectedGoal || !depositAmount) return;
    
    setSubmitting(true);
    try {
      const res = await api.post(`/savings-goals/${selectedGoal.id}/deposit`, {
        amount: parseFloat(depositAmount),
        note: depositNote || null
      });
      
      if (res.data.completed) {
        toast.success("🎉 ¡Meta completada!");
      } else {
        toast.success("Depósito registrado exitosamente");
      }
      
      setDepositDialogOpen(false);
      setDepositAmount("");
      setDepositNote("");
      setSelectedGoal(null);
      fetchGoals();
    } catch (error) {
      toast.error("Error al registrar depósito");
    } finally {
      setSubmitting(false);
    }
  };

  const openDepositDialog = (goal) => {
    setSelectedGoal(goal);
    setDepositDialogOpen(true);
  };

  const openHistoryDialog = async (goal) => {
    try {
      const res = await api.get(`/savings-goals/${goal.id}/deposits`);
      setSelectedGoal({ ...goal, ...res.data });
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error("Error al cargar historial");
    }
  };

  const handleSaveAllocations = async () => {
    const total = Object.values(allocations).reduce((sum, val) => sum + val, 0);
    if (total > 100) {
      toast.error("El total no puede superar 100%");
      return;
    }
    
    setSubmitting(true);
    try {
      const allocationsList = goals.map(g => ({
        goal_id: g.id,
        percentage: allocations[g.id] || 0
      }));
      
      await api.put("/savings-goals/allocations", { allocations: allocationsList });
      toast.success("Asignaciones guardadas");
      setAllocationDialogOpen(false);
      fetchGoals();
    } catch (error) {
      toast.error("Error al guardar asignaciones");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return formatMoney(amount, userCurrency);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalAllocated = Object.values(allocations).reduce((sum, val) => sum + val, 0);
  const totalSaved = goals.reduce((sum, g) => sum + (g.current_amount || 0), 0);
  const totalTarget = goals.reduce((sum, g) => sum + (g.target_amount || 0), 0);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 dark:bg-slate-800 rounded w-48"></div>
        <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded-2xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Metas de Ahorro</h1>
          <p className="text-slate-500 text-sm">Planifica y alcanza tus objetivos financieros</p>
        </div>
        <div className="flex gap-2">
          {goals.length > 0 && (
            <Button 
              variant="outline"
              onClick={() => setAllocationDialogOpen(true)}
            >
              <PieChart className="w-4 h-4 mr-2" />
              Asignar %
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={(open) => (!open || !isExpired) && setDialogOpen(open)}>
            <DialogTrigger asChild>
              <Button className="btn-accent" disabled={isExpired}>
                <Plus className="w-4 h-4 mr-2" />
                Nueva Meta
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-cyan-600" />
                  Nueva Meta de Ahorro
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la meta *</Label>
                  <Input
                    id="name"
                    placeholder="Ej: Carro, Vacaciones, Emergencias"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="target">Monto objetivo *</Label>
                  <CurrencyInput
                    id="target"
                    currency={userCurrency}
                    placeholder="0"
                    value={formData.target_amount}
                    onChange={(e) => setFormData({...formData, target_amount: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deadline">Fecha objetivo (opcional)</Label>
                  <Input
                    id="deadline"
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                  />
                </div>
                
                <Button 
                  className="w-full btn-primary"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Crear Meta"
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Card */}
      {goals.length > 0 && (
        <Card className="stat-card bg-gradient-to-r from-cyan-50 to-emerald-50 dark:from-cyan-900/20 dark:to-emerald-900/20">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-xs font-medium text-slate-500">Total Ahorrado</p>
                <p className="text-xl sm:text-2xl font-bold text-emerald-600">{formatCurrency(totalSaved)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Objetivo Total</p>
                <p className="text-xl sm:text-2xl font-bold text-cyan-600">{formatCurrency(totalTarget)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">% Asignado del Ahorro</p>
                <p className={`text-xl sm:text-2xl font-bold ${totalAllocated > 100 ? 'text-rose-500' : 'text-violet-600'}`}>
                  {totalAllocated}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Goals Grid */}
      {goals.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {goals.map((goal, index) => {
            const progress = goal.target_amount > 0 
              ? Math.min(100, (goal.current_amount / goal.target_amount) * 100) 
              : 0;
            const isCompleted = goal.current_amount >= goal.target_amount;
            
            return (
              <Card 
                key={goal.id} 
                className={`stat-card overflow-hidden ${isCompleted ? 'ring-2 ring-emerald-500' : ''}`}
              >
                <div 
                  className="h-1" 
                  style={{ backgroundColor: GOAL_COLORS[index % GOAL_COLORS.length] }}
                />
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <Target 
                        className="w-5 h-5" 
                        style={{ color: GOAL_COLORS[index % GOAL_COLORS.length] }}
                      />
                      <CardTitle className="text-base font-semibold">
                        {goal.name}
                        {isCompleted && <span className="ml-2 text-xs">🎉</span>}
                      </CardTitle>
                    </div>
                    {!isExpired && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-rose-500"
                        onClick={() => handleDelete(goal.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-bold text-lg">{formatCurrency(goal.current_amount)}</span>
                      <span className="text-slate-500">{formatCurrency(goal.target_amount)}</span>
                    </div>
                    <Progress 
                      value={progress} 
                      className="h-3"
                    />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>{progress.toFixed(0)}% completado</span>
                      {goal.allocation_percentage > 0 && (
                        <span className="text-violet-600">{goal.allocation_percentage}% asignado</span>
                      )}
                    </div>
                  </div>

                  {/* Deadline */}
                  {goal.deadline && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Calendar className="w-3 h-3" />
                      Meta: {formatDate(goal.deadline)}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                    <Button
                      size="sm"
                      className="flex-1 text-xs btn-primary"
                      onClick={() => openDepositDialog(goal)}
                      disabled={isCompleted || isExpired}
                    >
                      <DollarSign className="w-3 h-3 mr-1" />
                      Abonar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1 text-xs"
                      onClick={() => openHistoryDialog(goal)}
                    >
                      <History className="w-3 h-3 mr-1" />
                      Historial
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="stat-card">
          <CardContent className="p-12 text-center">
            <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No tienes metas de ahorro</p>
            <p className="text-sm text-slate-400 mb-6">
              Crea tu primera meta y empieza a ahorrar para alcanzar tus sueños
            </p>
            <Button onClick={() => !isExpired && setDialogOpen(true)} className="btn-accent" disabled={isExpired}>
              <Plus className="w-4 h-4 mr-2" />
              Crear primera meta
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Deposit Dialog */}
      <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              Abonar a Meta
            </DialogTitle>
          </DialogHeader>
          {selectedGoal && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <p className="text-sm font-medium">{selectedGoal.name}</p>
                <p className="text-xs text-slate-500">
                  Ahorrado: {formatCurrency(selectedGoal.current_amount)} de {formatCurrency(selectedGoal.target_amount)}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="deposit-amount">Monto a depositar</Label>
                <CurrencyInput
                  id="deposit-amount"
                  currency={userCurrency}
                  placeholder="0"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="deposit-note">Nota (opcional)</Label>
                <Input
                  id="deposit-note"
                  placeholder="Ej: Ahorro de enero"
                  value={depositNote}
                  onChange={(e) => setDepositNote(e.target.value)}
                />
              </div>
              
              <Button 
                className="w-full btn-primary"
                onClick={handleAddDeposit}
                disabled={submitting || !depositAmount}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Registrar Depósito"
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-cyan-500" />
              Historial de Depósitos
            </DialogTitle>
          </DialogHeader>
          {selectedGoal && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <p className="font-bold">{selectedGoal.goal_name || selectedGoal.name}</p>
                <div className="mt-2">
                  <Progress 
                    value={selectedGoal.progress || 0} 
                    className="h-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedGoal.progress?.toFixed(0) || 0}% completado
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600">Depósitos realizados:</p>
                {selectedGoal.deposits && selectedGoal.deposits.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedGoal.deposits.map((deposit, idx) => (
                      <div 
                        key={deposit.id || idx} 
                        className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex justify-between items-center"
                      >
                        <div>
                          <p className="font-medium text-emerald-700 dark:text-emerald-400">
                            {formatCurrency(deposit.amount)}
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(deposit.date)}
                          </p>
                          {deposit.note && (
                            <p className="text-xs text-slate-400 mt-1">{deposit.note}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">
                    No hay depósitos registrados aún
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Allocation Dialog */}
      <Dialog open={allocationDialogOpen} onOpenChange={setAllocationDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-violet-500" />
              Asignar % del Ahorro Mensual
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Define qué porcentaje de tu ahorro mensual (utilidades) se destinará a cada meta.
            </p>
            
            <div className="space-y-4 max-h-60 overflow-y-auto">
              {goals.map((goal, index) => (
                <div key={goal.id} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: GOAL_COLORS[index % GOAL_COLORS.length] }}
                      />
                      {goal.name}
                    </span>
                    <span className="text-sm font-bold">{allocations[goal.id] || 0}%</span>
                  </div>
                  <Slider
                    value={[allocations[goal.id] || 0]}
                    onValueChange={(value) => setAllocations({...allocations, [goal.id]: value[0]})}
                    max={100}
                    step={5}
                    className="w-full"
                  />
                </div>
              ))}
            </div>
            
            <div className={`p-3 rounded-lg ${totalAllocated > 100 ? 'bg-rose-50 dark:bg-rose-900/20' : 'bg-slate-50 dark:bg-slate-800'}`}>
              <div className="flex justify-between items-center">
                <span className="text-sm">Total asignado:</span>
                <span className={`font-bold ${totalAllocated > 100 ? 'text-rose-500' : 'text-violet-600'}`}>
                  {totalAllocated}%
                </span>
              </div>
              {totalAllocated > 100 && (
                <p className="text-xs text-rose-500 mt-1">El total no puede superar 100%</p>
              )}
              {totalAllocated < 100 && (
                <p className="text-xs text-slate-500 mt-1">{100 - totalAllocated}% sin asignar</p>
              )}
            </div>
            
            <Button 
              className="w-full btn-primary"
              onClick={handleSaveAllocations}
              disabled={submitting || totalAllocated > 100}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Guardar Asignaciones"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavingsPage;
